import React, { useState } from 'react';

import { API_MUTATION_DEBOUNCE_WAIT_TIME_IN_MILLISECONDS } from '@netfront/common-library';
import { useDebouncedCallback } from 'use-debounce';

import { DEFAULT_MESSAGE, DEFAULT_API_SUCCESS_MESSAGE, DEFAULT_API_ERROR_MESSAGE } from '../QuestionSnippet.constants';
import { handleApolloError } from '../QuestionSnippet.handlers';
import { saveContentSnippetQuestionAndApplyQuestionActions } from '../QuestionSnippet.helpers';

import { QuestionAnswerNumberSnippetProps } from './QuestionAnswerNumberSnippet.interfaces';

import {
  CONTENT_PAGE_CONTEXT_GRAPHQL,
  getContentSnippet,
  getContentSnippetFormId,
  ISetUserAnswerMutationGraphQLResponse,
  ISetUserAnswerNumberMutationVariables,
  useContentPageContext,
} from '../../../../contexts';
import { useEkardoMutation } from '../../../../hooks';
import { IContentPage, IQuestionAnswerNumber, IQuestionConfigurationNumber } from '../../../../interfaces';
import { NumberSnippet } from '../../NumberSnippet';
import { getMutationBaseVariables } from '../../Snippets.helpers';
import { IMessage } from '../../Snippets.interfaces';

const QuestionAnswerNumberSnippet = ({
  accessToken,
  contentSnippetId,
  contentSnippetQuestion,
  userFlowStepTrackId,
}: QuestionAnswerNumberSnippetProps) => {
  const { dispatch, state: contentPage } = useContentPageContext();
  const { actions, id: contentPageId } = contentPage as IContentPage;

  const contentSnippet = contentSnippetQuestion ?? getContentSnippet(contentSnippetId, contentPage);
  const contentSnippetFormId = getContentSnippetFormId(contentSnippetId, contentPage);

  const { answer, configuration, mandatory: isMandatory = false } = contentSnippet ?? {};
  const { numberAnswered } = answer ?? ({} as IQuestionAnswerNumber);
  const { defaultValue, description, maxValue, minValue } = configuration ?? ({} as IQuestionConfigurationNumber);

  const [message, setMessage] = useState<IMessage>(DEFAULT_MESSAGE);
  const [savedNumber, setSaveNumber] = useState<number>(numberAnswered);

  const [executeSetUserAnswerNumber] = useEkardoMutation<ISetUserAnswerMutationGraphQLResponse, ISetUserAnswerNumberMutationVariables>({
    mutation: CONTENT_PAGE_CONTEXT_GRAPHQL.mutations.setUserAnswerNumber,
    options: {
      onCompleted: () => {
        saveContentSnippetQuestionAndApplyQuestionActions({
          actions,
          answer,
          contentPage,
          contentSnippetId,
          dispatch,
          selectedQuestionAnswers: {
            numberAnswered: savedNumber,
          },
        });

        dispatch({
          type: 'updateQuestionAnswerNumberTotals',
        });

        setMessage({
          error: '',
          success: DEFAULT_API_SUCCESS_MESSAGE,
        });
      },
      onError: (error) => {
        handleApolloError(error);
        setMessage({
          error: DEFAULT_API_ERROR_MESSAGE,
          success: '',
        });
      },
    },
    token: accessToken,
  });

  const clearMessage = () => {
    setMessage(DEFAULT_MESSAGE);
  };

  const debouncedMutation = useDebouncedCallback((inputNumber: number) => {
    if (!contentSnippetFormId) {
      return;
    }

    const baseVariables = getMutationBaseVariables(contentPageId, contentSnippetFormId, contentSnippetId, Number(userFlowStepTrackId));

    const variables: ISetUserAnswerNumberMutationVariables = {
      ...baseVariables,
      numberAnswered: inputNumber,
    };

    executeSetUserAnswerNumber({
      variables,
    });
  }, API_MUTATION_DEBOUNCE_WAIT_TIME_IN_MILLISECONDS);

  const handleNumberChange = (inputNumber: number) => {
    clearMessage();
    debouncedMutation(inputNumber);
    setSaveNumber(inputNumber);
  };

  const isSummary = Boolean(contentSnippetQuestion);

  return (
    <NumberSnippet
      defaultValue={defaultValue}
      id={`input-number-${contentSnippetId}`}
      isDisabled={isSummary}
      isRequired={isMandatory}
      maxValue={maxValue}
      message={message}
      minValue={minValue}
      placeholder={description}
      value={savedNumber}
      onChange={(value) => handleNumberChange(value)}
    />
  );
};

export { QuestionAnswerNumberSnippet };
